import { LoanStatusEnum, type UserMortgage } from '@mr-website/schemas';
import { MapPin } from 'lucide-react';
import * as React from 'react';

import { api } from '@/api/client';
import { BookmarkSavedDate } from '@/components/blocks/BookmarkSavedDate';
import { DataCardBadge, type DataBadgeVariant } from '@/components/blocks/DataCard';
import { DataCard } from '@/components/blocks/DataCard';
import { DataCardContent } from '@/components/blocks/DataCard';

export interface MortgageCardProps {
  mortgage: UserMortgage;
  className?: string;
  onDeleted?: () => void;
  bookmarkIconVariant?: 'star' | 'prohibit';
  showBookmark?: boolean;
}

function mortgageStatusBadgeVariant(status: string): DataBadgeVariant {
  switch (status) {
    case LoanStatusEnum.AVAILABLE:
      return 'cyan';
    case LoanStatusEnum.PENDING:
      return 'orange';
    case LoanStatusEnum.HOLD:
      return 'blue';
    case LoanStatusEnum.DRAFT:
      return 'sky';
    case LoanStatusEnum.REMOVED:
      return 'rose';
    case LoanStatusEnum.DELETED:
      return 'slate';
    case LoanStatusEnum.FUNDED:
      return 'violet';
    case LoanStatusEnum.PAID:
      return 'emerald';
    case LoanStatusEnum.PROCESS:
      return 'fuchsia';
    case LoanStatusEnum.UNKNOWN:
    default:
      return 'default';
  }
}

function getOrdinalSuffix(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

export function MortgageCard({
  mortgage,
  className,
  onDeleted,
  bookmarkIconVariant,
  showBookmark = true,
}: MortgageCardProps) {
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleRemove = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await api.delete(`/user/mortgages/${mortgage.id}`);
      onDeleted?.();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DataCard
      imageSrc={mortgage.photo}
      imageAlt={`${mortgage.street ?? ''}, ${mortgage.city ?? ''}`}
      imageFallbackSrc="/static/images/nophoto-large.jpg"
      href={`/mortgage/${mortgage.loanId}`}
      badgeText={mortgage.loanStatus}
      badgeVariant={mortgageStatusBadgeVariant(mortgage.loanStatus ?? '')}
      showBookmarkIcon={showBookmark}
      isBookMarked={showBookmark}
      bookmarkIconVariant={bookmarkIconVariant}
      onBookmarkClick={showBookmark ? handleRemove : undefined}
      bookmarkLabel={showBookmark ? 'Remove from list' : undefined}
      className={className}
    >
      <DataCardContent>
        <span className="text-data-card-xl font-bold">
          {mortgage.price ? `$${mortgage.price.toLocaleString()}` : '$--'}
        </span>

        {/* Address */}
        <div>
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-text-muted" />
            <span
              className="truncate text-data-card-md font-semibold text-text-address"
              title={mortgage.street}
            >
              {mortgage.street}
            </span>
          </div>
          <span className="pl-5 text-data-card-subtitle text-data-card-xs tracking-wide">
            {mortgage.city}, {mortgage.state}
          </span>
        </div>

        {mortgage.is_user_lender && <DataCardBadge color="teal">You're funding this</DataCardBadge>}

        <div className="grid grid-cols-2 gap-4">
          <div className="min-w-0">
            <span className="block text-data-card-xs text-text-muted">Lien Position</span>
            <span className="block text-data-card-md font-semibold text-text-address">
              {mortgage.position
                ? `${mortgage.position}${getOrdinalSuffix(mortgage.position)}`
                : '--'}
            </span>
          </div>
          <div className="min-w-0 border-l border-card-border pl-4">
            <span className="block text-data-card-xs text-text-muted">Yield</span>
            <span className="block text-data-card-md font-semibold text-text-address">
              {mortgage.yield ? `${mortgage.yield.toFixed(2)}%` : '--'}
            </span>
          </div>
        </div>

        {showBookmark && <BookmarkSavedDate date={mortgage.date} />}
      </DataCardContent>
    </DataCard>
  );
}
